/**
 * Bezetting Page (Epic C-4 — Bezetting & conflicten, Story C-4.1, UX-C4)
 *
 * Week-navigatie + segmented control Week/Dag + blok 1 "Per taak" + blok 2
 * "Per medewerker (benutting)" + states. Route `/planning/bezetting`
 * (nav-integratie volgt in Story C-5.2, zelfde incrementele opleverpatroon
 * als `/planning/planbord`). Ook bereikbaar via de bezettingsstrook-doorklik
 * op het planbord (C-3.3/C-4.1 AC 4).
 *
 * Scope server-side (`planningService.getOccupancy`, AD-16, via `getBoard`):
 * leidinggevende ziet eigen team, HR/admin org-breed met optioneel
 * afdeling-filter — de afdeling-selector hieronder is daarom alleen
 * zichtbaar voor HR/admin (zelfde precedent als `PlanbordPage.tsx`).
 *
 * AD-13 (bindend): benutting is ALTIJD neutraal-informatief (`Progressbar`
 * draagt zelf geen kleur-prop) — géén rood bij lage of volle benutting.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth/AuthContext';
import { useDepartments } from '../settings/useSettings';
import { useOccupancy } from './hooks/useOccupancy';
import { SegmentedControl } from './components/SegmentedControl';
import { Progressbar } from './components/Progressbar';
import { getMondayOfWeek, getWorkweekDates, addWeeks, toDateString, formatWeekRange, formatDateLabel } from '../werkrooster/utils/dateUtils';
import type { PlanningOccupancyGranularity } from '@functieloonhuis/shared';

const GRANULARITY_OPTIONS = [
  { value: 'week' as const, label: 'Week' },
  { value: 'dag' as const, label: 'Dag' },
];

function initialWeekStart(searchParams: URLSearchParams): Date {
  const van = searchParams.get('van');
  if (van) {
    const parsed = new Date(`${van}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return getMondayOfWeek(parsed);
  }
  return getMondayOfWeek(new Date());
}

export function BezettingPage() {
  const { hasRole } = useAuth();
  const isHrOrAdmin = hasRole(['hr', 'admin']);

  const [searchParams] = useSearchParams();
  const [weekStart, setWeekStart] = useState<Date>(() => initialWeekStart(searchParams));
  const [departmentId, setDepartmentId] = useState<number | undefined>(undefined);
  const [granularity, setGranularity] = useState<PlanningOccupancyGranularity>('week');

  const dates = getWorkweekDates(weekStart).map(toDateString);
  const van = dates[0]!;
  const tot = dates[dates.length - 1]!;

  const { data: departments } = useDepartments();
  const { data: occupancy, isLoading, isError } = useOccupancy(van, tot, isHrOrAdmin ? departmentId : undefined, granularity);

  const goPrev = () => setWeekStart(addWeeks(weekStart, -1));
  const goNext = () => setWeekStart(addWeeks(weekStart, 1));
  const goToday = () => setWeekStart(getMondayOfWeek(new Date()));

  return (
    <Layout>
      <div data-testid="bezetting-page" className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1E293B]">Bezetting</h1>
          <p className="mt-1 text-sm text-gray-500">Zie in één oogopslag hoeveel dagdelen per taak zijn ingedeeld en hoe elk teamlid benut is.</p>
        </div>

        <div className="bg-white rounded-lg border border-[#E2E8F0]">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button type="button" onClick={goPrev} aria-label="Vorige week" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button type="button" onClick={goToday} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 border-x border-gray-200">
                  Vandaag
                </button>
                <button type="button" onClick={goNext} aria-label="Volgende week" className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-100">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <span className="text-sm font-medium text-gray-900">{formatWeekRange(weekStart)}</span>
              <SegmentedControl options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} ariaLabel="Weergave" />
            </div>

            {isHrOrAdmin && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Afdeling:</span>
                <select
                  data-testid="bezetting-afdeling-selector"
                  value={departmentId ?? ''}
                  onChange={(e) => setDepartmentId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
                >
                  <option value="">Alle</option>
                  {(departments ?? []).map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isLoading ? (
            <div data-testid="bezetting-skeleton" className="p-4 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-8 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : isError || !occupancy ? (
            <div className="text-center py-8 text-sm text-gray-500">Kon de bezetting niet laden.</div>
          ) : (
            <>
              {/* ── Blok 1 — Per taak ─────────────────────────────────── */}
              <div className="p-4 border-b border-[#E2E8F0]">
                <h2 className="text-sm font-semibold text-[#1E293B] mb-3">Per taak</h2>
                {occupancy.tasks.length === 0 ? (
                  <p className="text-sm text-gray-500">Nog geen taken.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left px-2 py-1 text-xs font-medium text-gray-500">Taak</th>
                        {dates.map((date) => (
                          <th key={date} className="px-1 py-1 text-[10px] font-medium text-gray-500 text-center min-w-[60px]">
                            {formatDateLabel(new Date(`${date}T00:00:00`))}
                          </th>
                        ))}
                        <th className="px-2 py-1 text-[10px] font-medium text-gray-500 text-right">Totaal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {occupancy.tasks.map((task) => (
                        <tr
                          key={task.taskId}
                          data-testid={`occupancy-taak-${task.taskId}`}
                          data-archived={task.isArchived}
                          className={`border-t border-[#E2E8F0] ${task.isArchived ? 'opacity-50' : ''}`}
                        >
                          <td className="px-2 py-1.5 flex items-center gap-1.5">
                            <span aria-hidden="true" className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: task.taskColor ?? '#E2E8F0' }} />
                            {task.taskName}
                          </td>
                          {task.days.map((day) => (
                            <td key={day.date} className="px-1 py-1.5 text-center text-[#1E293B]">
                              {granularity === 'dag' ? `${day.count.ochtend} / ${day.count.middag}` : day.count.ochtend + day.count.middag}
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right font-medium text-[#1E293B]">{task.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* ── Blok 2 — Per medewerker (benutting) ──────────────── */}
              <div className="p-4">
                <h2 className="text-sm font-semibold text-[#1E293B] mb-3">Per medewerker (benutting)</h2>
                {occupancy.employees.length === 0 ? (
                  <p className="text-sm text-gray-500">Geen teamleden gevonden voor deze afdeling.</p>
                ) : (
                  <div className="space-y-3">
                    {occupancy.employees.map((emp) => {
                      const nogBeschikbaar = Math.max(0, emp.available - emp.assigned);
                      return (
                        <div key={emp.employeeId} data-testid={`benutting-rij-${emp.employeeId}`} className="flex items-center gap-4">
                          <span className="w-40 flex-shrink-0 text-sm text-[#1E293B] truncate">{emp.employeeName}</span>
                          <div className="flex-1">
                            <Progressbar
                              data-testid={`benutting-progressbar-${emp.employeeId}`}
                              value={emp.assigned}
                              max={emp.available}
                              label={`${emp.assigned} van ${emp.available} dagdelen`}
                              ariaLabel={`Benutting ${emp.employeeName}: ${emp.assigned} van ${emp.available} dagdelen`}
                            />
                          </div>
                          <span className="w-32 flex-shrink-0 text-xs text-gray-500 text-right">nog beschikbaar: {nogBeschikbaar} dagdelen</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
